import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { Check, Star, Sparkles, Loader2, ArrowLeft } from "lucide-react";
import NumberFlow from "@number-flow/react";
import confetti from "canvas-confetti";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { supabase } from "@/integrations/supabase/client";
import { STRIPE_TIERS } from "@/constants/stripeTiers";
import { toast } from "@/hooks/use-toast";
import { logEdgeFunctionError } from "@/utils/sentry";
import { Card } from "@/components/ui/card";
import { getStripe } from "@/lib/stripe";

interface OnboardingPricingStepProps {
  initialPlan?: { planName: string; billing: "monthly" | "yearly" } | null;
  onBack: () => void;
  /** When false, hide the 5-day-trial CTA copy — Stripe charges immediately for returning customers. */
  eligibleForTrial?: boolean;
}

interface PricingPlan {
  name: string;
  price: string;
  yearlyPrice: string;
  yearlySavings: string;
  period: string;
  features: string[];
  description: string;
  isPopular: boolean;
  extraFeatures?: string[];
}

const PLANS: PricingPlan[] = [
  {
    name: "Starter",
    price: "59",
    yearlyPrice: "47",
    yearlySavings: "149",
    period: "miesiąc",
    features: [
      "Kreator scenariuszy Basic → 15 scenariuszy/mies.",
      "Kreator scenariuszy Pro → 10 scenariuszy/mies.",
      "Gotowe scenariusze wideo → do 3 źródeł inspiracji",
      "Śledzenie konkurencji → do 3 profili",
      "Analityka wzrostu wyprzedzająca Instagram o 24h",
      "Biblioteka pomysłów → 30 ostatnich dni",
      "Prompter → nagrywaj bez przeszkód, bez limitu",
      "Każdy scenariusz: 10 hooków + 10 nakładek tekstowych",
      "Do 5 zaskakujących faktów na scenariusz",
      "Fact-checking treści w scenariuszach",
    ],
    description: "Dla małych firm i twórców",
    isPopular: false,
  },
  {
    name: "Pro",
    price: "149",
    yearlyPrice: "112",
    yearlySavings: "449",
    period: "miesiąc",
    features: [
      "Kreator scenariuszy Basic → 45 scenariuszy/mies. (3× więcej niż Starter)",
      "Kreator scenariuszy Pro → 50 scenariuszy/mies. (5× więcej niż Starter)",
      "Gotowe scenariusze wideo → do 6 źródeł inspiracji (2× więcej niż Starter)",
      "Śledzenie konkurencji → do 6 profili (2× więcej niż Starter)",
      "Analityka wzrostu wyprzedzająca Instagram o 24h",
      "Biblioteka pomysłów → bez limitu",
      "Prompter → nagrywaj bez przeszkód, bez limitu",
      "Każdy scenariusz: 10 hooków + 10 nakładek tekstowych",
      "Do 5 zaskakujących faktów na scenariusz",
      "Fact-checking treści w scenariuszach",
    ],
    description: "Dla rozwijających się firm",
    isPopular: true,
  },
];

type StepView = "plan-selection" | "checkout";

const OnboardingPricingStep = ({ initialPlan, onBack, eligibleForTrial = true }: OnboardingPricingStepProps) => {
  const [isMonthly, setIsMonthly] = useState(initialPlan?.billing !== "yearly");
  const [view, setView] = useState<StepView>("plan-selection");
  const [selectedPlan, setSelectedPlan] = useState<{ planName: string; billing: "monthly" | "yearly" } | null>(null);
  const [highlightedPlan, setHighlightedPlan] = useState<string>(initialPlan?.planName || "Pro");
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const switchRef = useRef<HTMLButtonElement>(null);

  const fetchClientSecret = useCallback(async (planName: string, billing: "monthly" | "yearly") => {
    const priceId = planName === "Starter"
      ? STRIPE_TIERS.starter[billing].priceId
      : STRIPE_TIERS.pro[billing].priceId;

    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: { priceId, embedded: true },
    });

    if (error || data?.error) {
      throw new Error(data?.error || error?.message || "Checkout failed");
    }
    if (!data?.clientSecret) throw new Error("No client secret returned");
    return data.clientSecret as string;
  }, []);

  const handleSelectPlan = async (planName: string) => {
    const billing = isMonthly ? "monthly" : "yearly";
    setSelectedPlan({ planName, billing });
    setLoadingCheckout(true);

    try {
      const secret = await fetchClientSecret(planName, billing);
      setClientSecret(secret);
      setView("checkout");
    } catch (err) {
      const message = logEdgeFunctionError('create-checkout', err, { planName, billing });
      toast({
        title: "Błąd",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoadingCheckout(false);
    }
  };

  const handleBackToPlans = () => {
    setView("plan-selection");
    setClientSecret(null);
    setSelectedPlan(null);
  };

  const handleToggle = (checked: boolean) => {
    setIsMonthly(!checked);
    if (checked && switchRef.current) {
      const rect = switchRef.current.getBoundingClientRect();
      confetti({
        particleCount: 40,
        spread: 50,
        origin: {
          x: (rect.left + rect.width / 2) / window.innerWidth,
          y: (rect.top + rect.height / 2) / window.innerHeight,
        },
        colors: ["#f97316", "#fb923c", "#fdba74"],
        ticks: 150,
        gravity: 1.2,
        decay: 0.94,
        startVelocity: 25,
        shapes: ["circle"],
      });
    }
  };

  if (view === "checkout") {
    return (
      <Card className="p-6 border border-gray-200/60 shadow-lg animate-in fade-in-0 slide-in-from-bottom-4 duration-500 bg-white/80">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={handleBackToPlans} className="text-gray-500 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-xl font-bold tracking-tight leading-tight text-gray-900">Finalizuj płatność</h2>
            {selectedPlan && (
              <p className="text-sm text-gray-500">
                Plan {selectedPlan.planName} — {selectedPlan.billing === "monthly" ? "miesięcznie" : "rocznie"}
              </p>
            )}
          </div>
        </div>

        {loadingCheckout || !clientSecret ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <EmbeddedCheckoutProvider stripe={getStripe()} options={{ clientSecret }}>
            <EmbeddedCheckout className="min-h-[400px]" />
          </EmbeddedCheckoutProvider>
        )}
      </Card>
    );
  }

  return (
    <Card className="p-6 md:p-8 bg-white/80 border border-white/60 shadow-lg rounded-2xl animate-in fade-in-0 slide-in-from-bottom-4 duration-500">
      <div className="text-center mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight leading-tight text-gray-900 mb-2">Wybierz swój plan</h1>
        <p className="text-gray-500 leading-relaxed">Zacznij korzystać z pełnych możliwości Viraluj</p>
      </div>

      <div className="flex items-center justify-center gap-3 mb-6">
        <span className={cn("text-sm font-medium transition-colors", isMonthly ? "text-gray-900" : "text-gray-400")}>
          Miesięcznie
        </span>
        <Switch
          ref={switchRef}
          checked={!isMonthly}
          onCheckedChange={handleToggle}
          className="data-[state=checked]:bg-orange-500"
        />
        <span className={cn("text-sm font-medium transition-colors", !isMonthly ? "text-gray-900" : "text-gray-400")}>
          Rocznie <span className="text-orange-500 font-bold text-xs">(Oszczędzasz do 25%)</span>
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {PLANS.map((plan) => {
          const isHighlighted = plan.name === highlightedPlan;
          const isPro = plan.name === "Pro";
          return (
            <div
              key={plan.name}
              className={cn(
                "relative flex flex-col rounded-2xl transition-all duration-300",
                isHighlighted
                  ? cn(
                      "border-2 shadow-xl",
                      isPro ? "border-orange-500 ring-4 ring-orange-500/10" : "border-[#0080FF] ring-4 ring-[#0080FF]/10"
                    )
                  : "border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5"
              )}
            >
              {isHighlighted && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className={cn(
                    "text-white text-xs px-4 py-1.5 font-bold rounded-full flex items-center gap-1.5 shadow-lg",
                    isPro ? "bg-orange-500" : "bg-[#0080FF]"
                  )}>
                    <Star className="w-3 h-3 fill-current" />
                    {plan.isPopular ? "Najpopularniejsze" : "Twój wybór"}
                  </span>
                </div>
              )}

              <div className="flex flex-col flex-1 p-5 md:p-6">
                <h3 className={cn(
                  "text-xl font-bold tracking-tight leading-tight mb-1",
                  isPro ? "text-orange-600" : "text-[#0080FF]"
                )}>
                  {plan.name}
                </h3>
                <p className="text-xs text-gray-500 mb-3">{plan.description}</p>

                <div className="flex items-baseline gap-1 mb-1">
                  {!isMonthly && plan.price !== plan.yearlyPrice && (
                    <span className="text-lg text-gray-400 line-through font-semibold mr-1">
                      {plan.price}
                    </span>
                  )}
                  <NumberFlow
                    value={parseInt(isMonthly ? plan.price : plan.yearlyPrice)}
                    format={{ useGrouping: false }}
                    transformTiming={{ duration: 400, easing: "ease-out" }}
                    className={cn(
                      "text-4xl font-black tabular-nums tracking-tight",
                      isPro ? "text-orange-600" : "text-[#0080FF]"
                    )}
                  />
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-500 font-medium">PLN / {plan.period}</span>
                    <span className="text-xs text-gray-400">cena końcowa</span>
                  </div>
                </div>
                {!isMonthly && (
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full w-fit mb-3">
                    Oszczędzasz {plan.yearlySavings} PLN
                  </span>
                )}

                <Button
                  disabled={loadingCheckout}
                  onClick={() => handleSelectPlan(plan.name)}
                  className={cn(
                    "w-full rounded-xl font-bold transition-all duration-300 active:scale-[0.98] my-4",
                    isPro
                      ? "bg-orange-500 hover:bg-orange-600 text-white py-5 shadow-md hover:shadow-lg"
                      : "bg-[#0080FF] hover:bg-[#006AD5] text-white py-5 shadow-md hover:shadow-lg"
                  )}
                >
                  {loadingCheckout ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : eligibleForTrial ? "Wypróbuj 5 dni za darmo" : "Wznów subskrypcję"}
                </Button>
                <p className="text-xs text-center text-gray-400 -mt-2 mb-2">
                  {eligibleForTrial
                    ? `5 dni za darmo, potem ${isMonthly ? plan.price : plan.yearlyPrice} PLN/${plan.period}`
                    : `Zapłać: ${isMonthly ? plan.price : plan.yearlyPrice} PLN/${isMonthly ? plan.period : "rok"}`}
                </p>

                <div className="h-px bg-gray-100 mb-4" />

                <ul className="space-y-3 mb-4 flex-1">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2.5">
                      <div className={cn(
                        "w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5",
                        isPro ? "bg-orange-100" : "bg-blue-100"
                      )}>
                        <Check className={cn("w-3 h-3", isPro ? "text-orange-600" : "text-[#0080FF]")} />
                      </div>
                      <span className="text-xs leading-relaxed text-gray-500">
                        {(() => {
                          const bonusMatch = feature.match(/\((\d+×\s*więcej)[^)]*\)/);
                          const cleanFeature = bonusMatch ? feature.replace(bonusMatch[0], "").trim() : feature;
                          return (
                            <>
                              {cleanFeature.includes("→") ? (
                                <>
                                  {cleanFeature.split("→")[0]}
                                  <span className={cn("font-bold", isPro ? "text-orange-600" : "text-[#0080FF]")}>
                                    →{cleanFeature.split("→")[1]}
                                  </span>
                                </>
                              ) : cleanFeature}
                              {bonusMatch && (
                                <span className="inline-flex ml-1.5 text-[10px] font-bold text-white bg-orange-500 px-1.5 py-0.5 rounded-full whitespace-nowrap align-middle">
                                  {bonusMatch[1]}
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </span>
                    </li>
                  ))}
                </ul>

              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-100">
        <Button
          variant="ghost"
          onClick={onBack}
          className="w-full text-gray-500 hover:text-gray-900"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Wstecz
        </Button>
      </div>
    </Card>
  );
};

export default OnboardingPricingStep;
